import { BaseSensorWebView } from '../../../base/view/BaseSensorWebView';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../../ui/gzForm/validators/max-length.validator';
import { EditIntegrationConstants } from '../../../../editIntegration/EditIntegrationConstants';
import { RegexValidator } from '../../../../../ui/gzForm/validators/regex.validator';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { GzButtonWebComponent } from '../../../../../ui/gzButton';
import {
  WEB_COMPONENT_GZ_LABELED_CONTENT, WEB_COMPONENT_GZ_TEXT, WEB_COMPONENT_GZ_TOOLTIP,
  WEB_COMPONENT_SENSOR_ATLASSIAN_JIRA_CONFLUENCE_GRID,
} from '../../../../../webcomponents.constants';
import { AtlassianJiraConfluenceGridWebComponent } from '../../atlassianJiraConfluenceGrid';
import { GzTitleWebComponent } from '../../../../../ui/gzTitle';
import { combineLatest, Subscription } from 'rxjs';
import { CustomizableValidator } from '../../../../../ui/gzForm/validators/customizable.validator';
import { EmailValidator } from '../../../../../ui/gzForm/validators/email.validator';
import { Sensor } from '../../../../common/types';
import { FontSizes } from '../../../../../types/common/types';
import { htmlEncode } from '../../../../../utils/escape.utils';

export class AtlassianJiraConfluenceView extends BaseSensorWebView {
  public formGroup: FormGroup;
  public jiraCheckboxFormGroup: FormGroup;
  public confluenceCheckboxFormGroup: FormGroup;
  public formGroupSubscription: Subscription | null = null;

  public dom: {
    $jiraCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $confluenceCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $organizationSiteDomain: GzFormFieldWebComponent;
    $productAdminEmail: GzFormFieldWebComponent;
    $productAdminApiToken: GzFormFieldWebComponent;
    $addToList: GzButtonWebComponent;
    $atlassianJiraConfluenceGrid: AtlassianJiraConfluenceGridWebComponent;
    $title: GzTitleWebComponent;
  };

  public static getSensorSpecifics(sensor: Sensor, dependencies: any): string {
    if (!sensor.specifics.domain) {
      return ``;
    }

    return `
      <${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${dependencies.translateFn('Domain')}" hasColon="true">
        <${WEB_COMPONENT_GZ_TOOLTIP} class="integration-sensor-network" fontSize="${FontSizes.PARAGRAPH_12}" tooltipText="${htmlEncode(sensor.specifics.domain)}">
          ${sensor.specifics.domain}
        </${WEB_COMPONENT_GZ_TOOLTIP}>
      </${WEB_COMPONENT_GZ_LABELED_CONTENT}>
      `;
  }

  public translateSensorConnectivityErrorCodes(errorCode: number): string {
    return this.stringsTranslator.translateSensorConnectivityErrorCodes(errorCode);
  }

  protected createFormGroup() {
    this.dom.$organizationSiteDomain.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.Domain.MaxLength,
        this.dependencies.translateFn('The organization domain must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.Domain.MaxLength,
        })
      ),
    ];
    this.dom.$productAdminEmail.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      EmailValidator('Please enter a valid email'),
      MaxLengthValidator(
        EditIntegrationConstants.AdminEmail.MaxLength,
        this.dependencies.translateFn('The admin email must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.AdminEmail.MaxLength,
        })
      ),
    ];

    this.dom.$productAdminApiToken.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.AdminApiKey.MaxLength,
        this.dependencies.translateFn('The API token must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.AdminApiKey.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The API token cannot contain spaces')),
    ];

    this.dom.$jiraCheckbox.Validators = [
      CustomizableValidator(value => {
        if (value) {
          return {
            valid: true,
          };
        }
        return {
          valid: false,
          error: '',
        };
      }),
    ];

    this.dom.$confluenceCheckbox.Validators = [
      CustomizableValidator(value => {
        if (value) {
          return {
            valid: true,
          };
        }
        return {
          valid: false,
          error: '',
        };
      }),
    ];

    this.formGroup = new FormGroup({
      domain: this.dom.$organizationSiteDomain,
      admin_email: this.dom.$productAdminEmail,
      api_token: this.dom.$productAdminApiToken,
    });

    this.jiraCheckboxFormGroup = new FormGroup({
      jira_checkbox: this.dom.$jiraCheckbox,
    });

    this.confluenceCheckboxFormGroup = new FormGroup({
      confluence_checkbox: this.dom.$confluenceCheckbox,
    });

    this.formGroupSubscription = combineLatest([
      this.formGroup.valid$,
      this.jiraCheckboxFormGroup.valid$,
      this.confluenceCheckboxFormGroup.valid$,
    ]).subscribe({
      next: ([fieldsValid, jiraValid, confluenceValid]) => {
        this.dom.$addToList.disabled = !(fieldsValid && (jiraValid || confluenceValid));
      },
    });
    return;
  }

  getDomElements(): void {
    super.getDomElements();
    this.dom = {
      $title: this.shadow.querySelector('#jira-confluence-title'),
      $atlassianJiraConfluenceGrid: this.shadow.querySelector(WEB_COMPONENT_SENSOR_ATLASSIAN_JIRA_CONFLUENCE_GRID),
      $jiraCheckbox: this.shadow.querySelector('#jira_checkbox'),
      $confluenceCheckbox: this.shadow.querySelector('#confluence_checkbox'),
      $organizationSiteDomain: this.shadow.querySelector('#organization-site-domain'),
      $productAdminApiToken: this.shadow.querySelector('#product-admin-api-token'),
      $productAdminEmail: this.shadow.querySelector('#product-admin-email'),
      $addToList: this.shadow.querySelector('#add-to-list'),
      ...this.baseDom,
    };
    this.getTranslations();
  }

  getTranslations(): any {
    super.getTranslations();
    return this.translations;
  }

  setTranslatedStrings(): void {
    this.dom.$title.innerText = this.dependencies.translateFn('Jira and Confluence integrations');
    this.dom.$jiraCheckbox.Input.label = this.dependencies.translateFn('Jira');
    this.dom.$confluenceCheckbox.Input.label = this.dependencies.translateFn('Confluence');
    this.dom.$organizationSiteDomain.label = this.dependencies.translateFn('Organization site domain');
    this.dom.$productAdminEmail.label = this.dependencies.translateFn('Product admin email address');
    this.dom.$productAdminApiToken.label = this.dependencies.translateFn('Product admin API token');
  }

  public buildSuccessView(viewData): void {
    super.buildSuccessView(viewData);
    if (this.dom.$atlassianJiraConfluenceGrid.injectDependencies) {
      this.dom.$atlassianJiraConfluenceGrid.injectDependencies(this.dependencies);
      this.dom.$atlassianJiraConfluenceGrid
        .start()
        .then(async () => {
          await this.dom.$atlassianJiraConfluenceGrid.controller?.setData(viewData);
        })
        .catch();
    }
    this.dom.$addToList.textContent = this.dependencies.translateFn('Add to list');
    this.dom.$jiraCheckbox.Input.checked = true;
    this.dom.$confluenceCheckbox.Input.checked = true;
    this.dom.$addToList.setAttribute('disabled', 'true');
  }

  public resetFormFields(): void {
    this.formGroup.value = {
      domain: '',
      admin_email: '',
      api_token: '',
    };
  }

  protected async setViewData(viewData) {}

  public setEnabledAllFields(isEnabled: boolean): void {}
}
